import { toPixels, toRelative, type PlanPoint, type Size } from '@oscom/shared';

/**
 * Pointer travel (container px) up to which a press still counts as a click.
 * Above it the gesture is a pan (viewer) or a move (editor).
 */
export const DRAG_THRESHOLD_PX = 4;

/** screen = image pixel * scale + offset, in container coordinates. */
export interface Viewport {
  scale: number;
  x: number;
  y: number;
}

export interface ScaleLimits {
  min: number;
  max: number;
}

/** Whole image visible and centred, with a margin. */
export function fitViewport(container: Size, image: Size, margin = 16): Viewport {
  const scale = Math.max(
    Math.min(
      (container.width - 2 * margin) / image.width,
      (container.height - 2 * margin) / image.height,
    ),
    Number.EPSILON,
  );
  return {
    scale,
    x: (container.width - image.width * scale) / 2,
    y: (container.height - image.height * scale) / 2,
  };
}

/** From half the fitted size up to 8x the fitted size, at least 2x the native pixels. */
export function scaleLimits(fitScale: number): ScaleLimits {
  return { min: fitScale / 2, max: Math.max(fitScale * 8, 2) };
}

/** Zooms by `factor` and keeps the image point under `anchor` where it is. */
export function zoomAt(
  view: Viewport,
  factor: number,
  anchor: PlanPoint,
  limits: ScaleLimits,
): Viewport {
  const scale = Math.min(limits.max, Math.max(limits.min, view.scale * factor));
  const k = scale / view.scale;
  return { scale, x: anchor.x - (anchor.x - view.x) * k, y: anchor.y - (anchor.y - view.y) * k };
}

export function panBy(view: Viewport, dx: number, dy: number): Viewport {
  return { ...view, x: view.x + dx, y: view.y + dy };
}

/** Container point -> relative plan position, clamped to the image. */
export function screenToRelative(view: Viewport, image: Size, screen: PlanPoint): PlanPoint {
  return toRelative(
    { x: (screen.x - view.x) / view.scale, y: (screen.y - view.y) / view.scale },
    image,
  );
}

/** Relative plan position -> container point (markers keep their size). */
export function relativeToScreen(view: Viewport, image: Size, relative: PlanPoint): PlanPoint {
  const pixel = toPixels(relative, image);
  return { x: view.x + pixel.x * view.scale, y: view.y + pixel.y * view.scale };
}

/** Wheel delta -> zoom factor; smooth for touchpads, bounded for mouse wheels. */
export function wheelFactor(deltaY: number): number {
  return Math.exp(-Math.max(-300, Math.min(300, deltaY)) * 0.0015);
}
